import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useSelectTheme } from "../../store";
import { IParticipant, ProjectCollaboratorRole, WorkspaceCollaboratorRole } from "../../shared/db";
import { Button, ButtonCancel, Checkbox, TooltipHover } from "..";
import { CalendarGreyIcon, MessageWhiteIcon } from "../../asset/image/icon";
import { Scrollbars } from "react-custom-scrollbars-2";
import { RIF, _, collaboratorTypeHasRole, useCollaboratorTypeInProject } from "../../lib";
import { useParams } from "react-router-dom";

// Shown when the signed-in person can open the panel but cannot message participants.
const VIEWER_CANNOT_SEND_MESSAGE =
  "Viewers cannot send messages. If you want to send messages, please ask an admin to upgrade your role for this project.";

export type OverviewSidebarDataType = "heatmap" | "participantSetupStatus";
export enum OverviewSidebarTab {
  NotJoinedYet,
  NotStartedYet,
  HalfwaySetup,
  InCell,
  NotInCell,
}
export enum OverviewSidebarInCellTabTitle {
  Gt4 = "Data > 4hrs",
  Lt4 = "Data < 4hrs",
  NoData = "No Data",
  NotSyncedYet = "Not Synced Yet",
}

export const OverviewSidebar = (props: {
  participantList: IParticipant[];
  participantActivated: IParticipant[];
  garminConnectEnabled: boolean;
  garminDeviceEnabled: boolean;
  expandSidebar: boolean;
  sidebarDate: number;
  sidebarDatatype: OverviewSidebarDataType;
  sidebarDefaultTab: OverviewSidebarTab;
  sidebarInCellTabTitle: OverviewSidebarInCellTabTitle;
  sidebarDeviceType: "garminConnect" | "garminDirect" | null;
  participantNotJoinedTheProject: string[];
  participantNotSetupGarminConnect: string[];
  participantNotSetupLabfrontDirect: string[];
  participantInCell: string[];
  setSendNotificationParticipantIdList: Dispatch<SetStateAction<string[]>>;
  setDisplayPopupSendNotification: Dispatch<SetStateAction<boolean>>;
  setExpandSidebar: Dispatch<SetStateAction<boolean>>;
  setSelectedParticipantList: Dispatch<SetStateAction<string[]>>;
}) => {
  const {
    participantList,
    participantActivated,
    garminConnectEnabled,
    garminDeviceEnabled,
    expandSidebar,
    sidebarDate,
    sidebarDatatype,
    sidebarDefaultTab,
    sidebarInCellTabTitle,
    sidebarDeviceType,
    participantNotJoinedTheProject,
    participantNotSetupGarminConnect,
    participantNotSetupLabfrontDirect,
    participantInCell,
    setSendNotificationParticipantIdList,
    setDisplayPopupSendNotification,
    setExpandSidebar,
  } = props;
  const { color, fontSize, fontWeight } = useSelectTheme();
  const { projectId } = useParams();
  const role = useCollaboratorTypeInProject({ projectId });
  // A project collaborator's role is projectEditor, projectAdmin, or projectViewer.
  // Those are not the workspace strings editor, admin, and viewer, so an allowlist of
  // workspace roles matches nothing and this control never renders.
  const canSendMessage = collaboratorTypeHasRole({
    collaboratorType: role,
    projectRole: ProjectCollaboratorRole.ParticipantUpdate,
    workspaceRole: WorkspaceCollaboratorRole.ParticipantUpdate,
  });
  const [showViewerTooltip, setShowViewerTooltip] = useState(false);
  const participantNotInCell = useMemo(() => {
    return _.difference(
      participantActivated.map((p) => p.id),
      participantInCell,
    );
  }, [participantList, participantInCell]);
  const tabList = useMemo(() => {
    const result = [];
    if (sidebarDatatype === "heatmap") {
      result.push(sidebarDefaultTab);
      if (participantNotInCell.length > 0) {
        result.push(OverviewSidebarTab.NotInCell);
      }
    } else {
      if (participantNotJoinedTheProject.length > 0) {
        result.push(OverviewSidebarTab.NotJoinedYet);
      }
      if (
        (garminConnectEnabled &&
          garminDeviceEnabled &&
          participantNotSetupGarminConnect.length > 0) ||
        (garminConnectEnabled &&
          !garminDeviceEnabled &&
          participantNotSetupGarminConnect.length > 0) ||
        (!garminConnectEnabled &&
          garminDeviceEnabled &&
          participantNotSetupLabfrontDirect.length > 0)
      ) {
        result.push(OverviewSidebarTab.NotStartedYet);
      }
      if (
        garminConnectEnabled &&
        garminDeviceEnabled &&
        participantNotSetupLabfrontDirect.length > 0
      ) {
        result.push(OverviewSidebarTab.HalfwaySetup);
      }
    }
    return result;
  }, [sidebarDatatype, sidebarInCellTabTitle, sidebarDate, sidebarDeviceType]);

  const [tab, setTab] = useState<OverviewSidebarTab>(sidebarDefaultTab);
  const [selectedParticipant, setSelectedParticipant] = useState<
    Record<OverviewSidebarTab, string[]>
  >({
    [OverviewSidebarTab.NotJoinedYet]: [],
    [OverviewSidebarTab.NotStartedYet]: [],
    [OverviewSidebarTab.HalfwaySetup]: [],
    [OverviewSidebarTab.InCell]: [],
    [OverviewSidebarTab.NotInCell]: [],
  });

  // switch to default tab
  useMemo(() => {
    setTab(sidebarDefaultTab);
  }, [sidebarDefaultTab, sidebarDeviceType]);

  // select all participant by default
  useMemo(() => {
    setTab(sidebarDefaultTab);
    switch (sidebarDatatype) {
      case "heatmap":
        setSelectedParticipant({
          ...selectedParticipant,
          [OverviewSidebarTab.InCell]: participantInCell,
          [OverviewSidebarTab.NotInCell]: participantNotInCell,
        });
        break;
      case "participantSetupStatus": {
        setSelectedParticipant({
          ...selectedParticipant,
          [OverviewSidebarTab.NotJoinedYet]: participantNotJoinedTheProject,
          [OverviewSidebarTab.NotStartedYet]: garminConnectEnabled
            ? participantNotSetupGarminConnect
            : participantNotSetupLabfrontDirect,
          [OverviewSidebarTab.HalfwaySetup]:
            garminConnectEnabled && garminDeviceEnabled ? participantNotSetupLabfrontDirect : [],
        });
        break;
      }
    }
  }, [sidebarInCellTabTitle, sidebarDate, sidebarDatatype]);

  // update participantList when switching tabs
  const participantListByTab = useMemo(() => {
    if (!expandSidebar) return [];
    switch (tab) {
      case OverviewSidebarTab.NotJoinedYet:
        return participantList.filter((p) => participantNotJoinedTheProject.includes(p.id));
      case OverviewSidebarTab.NotStartedYet:
        if (garminConnectEnabled) {
          return participantList.filter((p) => participantNotSetupGarminConnect.includes(p.id));
        }
        return participantList.filter((p) => participantNotSetupLabfrontDirect.includes(p.id));
      case OverviewSidebarTab.HalfwaySetup:
        return participantList.filter((p) => participantNotSetupLabfrontDirect.includes(p.id));
      case OverviewSidebarTab.InCell:
        return participantList.filter((p) => participantInCell.includes(p.id));
      case OverviewSidebarTab.NotInCell:
        return participantList.filter((p) => participantNotInCell.includes(p.id));
    }
  }, [tab, sidebarInCellTabTitle, sidebarDate, sidebarDatatype, sidebarDefaultTab]);

  // check if all participants in current tab are selected
  const allSelected = useMemo(() => {
    const currentSelected = selectedParticipant[tab];
    return currentSelected.length === participantListByTab.length && currentSelected.length > 0;
  }, [selectedParticipant, tab, participantListByTab]);

  // select participant
  const updateSelectedParticipant = (pid: string) => {
    const currentSelected = [...selectedParticipant[tab]];
    const updatedSelected = currentSelected.includes(pid)
      ? currentSelected.filter((id) => id !== pid)
      : [...currentSelected, pid];
    setSelectedParticipant({ ...selectedParticipant, [tab]: updatedSelected });
  };

  const handleSelectAll = (e: boolean) => {
    const participantIdList = participantListByTab.map((p) => p.id);
    if (e) {
      setSelectedParticipant({ ...selectedParticipant, [tab]: participantIdList });
    } else {
      setSelectedParticipant({ ...selectedParticipant, [tab]: [] });
    }
  };

  const tabTitleMap = {
    [OverviewSidebarTab.NotJoinedYet]: "Not Joined Yet",
    [OverviewSidebarTab.NotStartedYet]: "Not Started Yet",
    [OverviewSidebarTab.HalfwaySetup]: "Halfway Setup",
    [OverviewSidebarTab.InCell]: sidebarInCellTabTitle,
    [OverviewSidebarTab.NotInCell]: "The Rest",
  };

  const submitBtnDisabled = selectedParticipant[tab].length === 0;

  const handleSubmit = () => {
    if (!canSendMessage || selectedParticipant[tab].length === 0) return;
    setSendNotificationParticipantIdList(selectedParticipant[tab]);
    setExpandSidebar(false);
    setDisplayPopupSendNotification(true);
  };

  const ParticipantList = () => {
    return (
      <>
        <div
          css={{
            padding: "16px",
            borderBottom: `1px solid ${color.border._160}`,
          }}
        >
          <label
            css={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <Checkbox value={allSelected} onChange={handleSelectAll} />
            <p
              css={{
                marginLeft: "8px",
                fontSize: fontSize.h7,
              }}
            >
              Select All
            </p>
          </label>
        </div>
        <div
          css={{
            flex: 1,
            padding: "16px",
          }}
        >
          <Scrollbars
            autoHide={false}
            style={{
              height: "100%",
              width: "100%",
            }}
          >
            {participantListByTab.map((participant: IParticipant) => (
              <ParticipantSelectorEntry
                {...{
                  key: participant.id,
                  participant,
                }}
              />
            ))}
          </Scrollbars>
        </div>
      </>
    );
  };

  const ParticipantSelectorEntry = (props: { participant: IParticipant }) => {
    const { participant } = props;
    const selected = useMemo(() => {
      return selectedParticipant[tab].includes(participant.id);
    }, [selectedParticipant, tab, participant]);

    return (
      <label
        css={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          marginBottom: "16px",
          cursor: "pointer",
        }}
      >
        <Checkbox value={selected} onChange={() => updateSelectedParticipant(participant.id)} />
        <p
          css={{
            marginLeft: "8px",
            fontSize: fontSize.h7,
            fontWeight: fontWeight.medium,
          }}
        >
          {participant.insignia}
        </p>
      </label>
    );
  };

  return (
    <div
      id="overview-sidebar"
      css={{
        width: expandSidebar ? "360px" : "0",
        height: "calc(100vh - 106px)",
        position: "fixed",
        right: 0,
        bottom: 0,
        boxShadow: "-8px 0px 36px 0px #C0C0C040",
        transition: "width .6s ease-in-out",
        backgroundColor: color.white,
        zIndex: 105,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      <div
        css={{
          width: "100%",
          padding: "16px",
          backgroundColor: color.surface.grey.dark,
        }}
      >
        <ButtonCancel
          bgColor={color.surface.grey.dark}
          ariaLabel={"Collapse sidebar"}
          onClick={() => setExpandSidebar(false)}
        />
        <p
          css={{
            fontSize: fontSize.h4,
            fontWeight: fontWeight.thick,
            width: "100%",
            margin: "16px 0",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {sidebarDatatype === "heatmap"
            ? "Participant Data Quality Details"
            : "Participant Setup Status Details"}
        </p>
        <div
          css={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <img
            width="18"
            css={{ marginRight: "8px" }}
            src={CalendarGreyIcon}
            alt={"Calendar icon"}
          />
          <p
            css={{
              fontSize: fontSize.h6,
              color: color.textIcon.secondary,
            }}
          >
            {sidebarDate}
          </p>
        </div>
      </div>
      <div
        css={{
          padding: "8px 16px 16px 16px",
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          css={{
            display: "flex",
            width: "100%",
            borderBottom: `1px solid ${color.border._160}`,
            gap: 20,
          }}
        >
          {tabList.map((t) => {
            return (
              <button
                type="button"
                key={t}
                onClick={() => setTab(t)}
                css={{
                  padding: "8px 0",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  borderBottom: tab === t ? `2px solid ${color.border.blue}` : "none",
                  color: tab === t ? color.primary : color.textIcon.secondary,
                }}
              >
                {tabTitleMap[t]}
              </button>
            );
          })}
        </div>
        <div
          css={{
            flex: 1,
            margin: "16px 0",
            border: `1px solid ${color.border._160}`,
            borderRadius: "5px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ParticipantList />
        </div>
        <div
          css={{ position: "relative", width: "100%", flexShrink: 0 }}
          title={canSendMessage ? undefined : VIEWER_CANNOT_SEND_MESSAGE}
          onMouseEnter={() => {
            if (!canSendMessage) setShowViewerTooltip(true);
          }}
          onMouseLeave={() => setShowViewerTooltip(false)}
        >
          <Button
            type="button"
            onClick={handleSubmit}
            css={{
              width: "100%",
              pointerEvents: canSendMessage ? "auto" : "none",
            }}
            btnPadding="mediumSmall"
            disabled={!canSendMessage || submitBtnDisabled}
            children={
              <span
                css={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img width={18} css={{ marginRight: "4px" }} src={MessageWhiteIcon} alt="" />
                <p
                  css={{
                    fontSize: fontSize.h6,
                    fontWeight: fontWeight.thick,
                    color: color.white,
                  }}
                >
                  {`Send Notification to ${selectedParticipant[tab].length} Participant(s)`}
                </p>
              </span>
            }
          />
          {RIF(
            showViewerTooltip,
            <TooltipHover
              {...{
                tooltipWidth: "240px",
                title: VIEWER_CANNOT_SEND_MESSAGE,
                hoverTopPosition: "-112px",
                pseudoTopPosition: "100%",
              }}
            />,
          )}
        </div>
      </div>
    </div>
  );
};
